import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, type ComponentPropsWithoutRef } from "react";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { formatIban, normalizeIban } from "@utils/formatting";

interface Props extends Omit<ComponentPropsWithoutRef<typeof Input>, "value" | "onChange"> {
  value: string;
  /** Compatible with RHF field.onChange. */
  onChange: (value: string) => void;
}

/** Position right after the `count`-th alphanumeric character, skipping the group space when moving forward. */
const caretAfter = (formatted: string, count: number, isDeleting: boolean): number => {
  let seen = 0;
  let position = 0;
  while (seen < count && position < formatted.length) {
    if (formatted[position] !== " ") seen++;
    position++;
  }
  return !isDeleting && formatted[position] === " " ? position + 1 : position;
};

/**
 * IBAN text input: no length limit (IBANs go up to 34 characters), always displayed by groups of
 * four, regrouped as the user types or pastes, keeping the caret on the character it was next to.
 * Submit-time compaction is done by each form's mapper with `normalizeIban`.
 */
export const IbanInput = forwardRef<HTMLInputElement, Props>(function IbanInput({ value, onChange, ...inputProps }, ref) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  // No dependency: the caret must also be restored when the formatted value did not change.
  useLayoutEffect(() => {
    if (pendingCaret.current === null || document.activeElement !== inputRef.current) return;
    inputRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  });

  return (
    <Input
      {...inputProps}
      ref={inputRef}
      value={formatIban(value)}
      onChange={e => {
        const inputType = (e.nativeEvent as InputEvent).inputType ?? "";
        const isDeleting = inputType.startsWith("delete");
        const caret = e.target.selectionStart ?? e.target.value.length;
        let before = normalizeIban(e.target.value.slice(0, caret));
        let after = normalizeIban(e.target.value.slice(caret));
        // Deleting a group space alone would be undone by the regrouping: delete the character beyond it instead.
        if (isDeleting && before.length + after.length === normalizeIban(value).length) {
          if (inputType === "deleteContentBackward") before = before.slice(0, -1);
          else after = after.slice(1);
        }
        const formatted = formatIban(before + after);
        pendingCaret.current = caretAfter(formatted, before.length, isDeleting);
        onChange(formatted);
      }}
    />
  );
});
